import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { ILoanStatusConfig, LoanStatusType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { Tooltip } from 'flowbite-react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, setAdminConfig } from 'services'
import { MultiSelect } from 'stories/components'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, fillObject, generateUniqeId, InputConvert, InputValidate, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { LoanServicingStatus } from './LoanServicingStatus'

const loanStatusCategories: Record<string, string> = {
  isActive: 'Active',
  isArchived: 'Archived',
  isClose: 'Closed',
  isLoanSetupUnhold: 'Loan Setup Unhold',
  isSuspended: 'Approval (Suspense)',
  isBrokerCanResubmitConditions: 'Can Broker resubmit conditions?',
}

const loanStatusColors: Record<string, string> = {
  isActive: 'green',
  isArchived: 'sky',
  isClose: 'yellow',
  isLoanSetupUnhold: 'orange',
  isSuspended: 'red',
  isBrokerCanResubmitConditions: 'purple',
}

const loanStatusDescriptions: Record<string, string> = {
  isActive: 'Active Loan Status which is used in Pipeline.',
  isArchived: 'Archived Loan Status which is used in Pipeline.',
  isClose:
    'Clear to Closed Loan Status. These loans can use Closing Calendar page and can use `Generate Commitment` in Loan Submission page.',
  isLoanSetupUnhold: 'Loan Status which can Submit loan in Loan Submission page.',
  isSuspended: 'Loan Status which can Generate Approval(Suspense) in Loan Submission page.',
  isBrokerCanResubmitConditions: 'Loan Status which can Submit Conditions in Loan Submission page.',
}

const LoanStatusLabel = () => {
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<Record<string, ILoanStatusConfig>>({})

  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [inputs, setInputs] = useState(defaultInputs(loanGlobalStatus))

  const defaultNonServicingStatus = fillObject(Object.keys(loanGlobalStatus), false)

  const initData = async () => {
    const [data, settingData] = await Promise.all([getAdminConfig('loanStatus'), getAdminConfig('globalSettingsV2')])
    setData(data)

    const newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = settingData[key]
    })
    setInputs(newInputs)
    setLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  const onSync = async (_data?: any) => {
    setLoading(true)
    await setAdminConfig('loanStatus', _data || data)
    setLoading(false)
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data)
    if (temp[key] === undefined) temp[key] = {} as any
    ;(temp[key] as any)[map] = value
    if (map === 'new' && value.length === 0) temp[key]['use'] = false
    setData(temp)
    if (!['fromPermission', 'toPermission'].includes(map)) onSync(temp)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return

    newData[newKey] = {
      origin: '',
      new: title,
      use: true,
      fromPermission: Object.keys(accountTypes) as any,
      toPermission: Object.keys(accountTypes) as any,
      isActive: false,
    }
    setData(newData)
    onSync(newData)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    onSync(newData)
  }

  const onChangeInput = async (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)

    const rtl: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      rtl[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    setLoading(true)
    await setAdminConfig('globalSettingsV2', rtl)
    setLoading(false)
  }

  const renderControls = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          if (['nonServicingLoanStatus', 'borrowerLoanStatus'].includes(key))
            input.value = { ...defaultNonServicingStatus, ...input.value }

          return (
            <div className={`input md:col-span-${input.span || 1} `} key={index}>
              <RenderInput input={input} Key={key} onChange={onChangeInput} onBlur={() => onSubmit()} />
            </div>
          )
        })}
      </div>
    )
  }

  const renderLoanStatusDescriptions = () => {
    return (
      <div className="mt-4 border rounded-lg p-4 bg-gray-100">
        {Object.keys(loanStatusDescriptions).map((key, index) => (
          <p className={`text-${loanStatusColors[key]}-500`}>
            {index + 1}. {loanStatusCategories[key]}: {loanStatusDescriptions[key]}
          </p>
        ))}
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      {renderControls()}

      <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
        <p>- Origination Pipeline</p>
        <div className="flex">
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
      <div className="mt-4">
        <table className="w-full text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Origin Status', 'New Status', 'In Use', 'From', 'To', 'Status'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const item = data[key] || {}
              const isCustom = !Object.values(LoanStatusType).includes(key as any)
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="px-3 border">{item.origin}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={item.new}
                      content={item.new}
                      onChange={(newValue: string) => onChange(key, newValue, 'new')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <div className="flex items-center">
                      <Checkbox
                        color="gray"
                        id={key}
                        title={''}
                        key={key}
                        value={item.use}
                        onChange={(value) => onChange(key, value, 'use')}
                      />
                      {isCustom && (
                        <div className="flex">
                          <span className="btn-icon" onClick={() => onRemove(key)}>
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 border text-center">
                    <MultiSelect
                      id={`${key}-fromPermission`}
                      title="Account Type"
                      options={accountTypes}
                      value={item.fromPermission || []}
                      onChange={(selected) => onChange(key, selected, 'fromPermission')}
                      onBlur={onSync}
                      className="w-64"
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <MultiSelect
                      id={`${key}-toPermission`}
                      title="Account Type"
                      options={accountTypes}
                      value={item.toPermission || []}
                      onChange={(selected) => onChange(key, selected, 'toPermission')}
                      onBlur={onSync}
                      className="w-64"
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <div className="grid grid-flow-col grid-rows-2 items-center gap-2">
                      {Object.keys(loanStatusCategories).map((type) => (
                        <Tooltip content={loanStatusCategories[type]} key={`${key}-${type}`}>
                          <Checkbox
                            color={loanStatusColors[type]}
                            id={`${key}-${type}`}
                            title={''}
                            value={(item as any)[type]}
                            onChange={(value) => onChange(key, value, type)}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {renderLoanStatusDescriptions()}
    </div>
  )
}

export const LoanStatusLabelPage = () => {
  return (
    <div>
      <LoanStatusLabel />
      <div className="mb-8" />
      <LoanServicingStatus />
    </div>
  )
}
